import { getMetaData } from "../../utils/Constant";
import { useEffect } from "react";

export default function FileThreat() {
  // Set title and metadata
  useEffect(() => {
    const metaData = getMetaData("3-file-type-meta-data");
    document.title = metaData.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = metaData.description; // Use .content instead of setAttribute
  }, []);
  
  return (
    <div className="container mt-5 mb-5">
      <article className="bg-white rounded-4 shadow-lg border-0 overflow-hidden">
        <div className="row justify-content-center">
          {/* Header */}
          <header
            className="text-center py-5 px-3 position-relative ps-5 pe-5"
            style={{
              color: "#fff",
              background: "linear-gradient(135deg, #dc3545 0%, #8b0000 100%)",
            }}
          >
            <h1 className="display-5 fw-bold text-white mb-3 lh-sm">
              Don't Get Hacked! 3 File Types You Should Never Open from a
              Stranger
            </h1>
            <div className="text-white mb-4">
              <span className="me-3">• Cyber Security</span>
              <span className="me-3">• Threat Analysis</span>
              <span>• Prevention Guide</span>
            </div>

            <div className="mt-4 position-relative">
              <span className="badge bg-light text-dark fs-6 me-2 p-2">
                Online Safety
              </span>
              <span className="badge bg-light text-dark fs-6 p-2">
                Malware Protection
              </span>
            </div>
          </header>

          <section className="p-4 p-md-5">
            {/* Executive Summary */}
            <section className="mb-5">
              <div className="card border-start-4 border-start-danger shadow-sm">
                <div className="card-body">
                  <h6 className="card-title text-uppercase fw-bold text-black mb-2">
                    Security Alert
                  </h6>
                  <p className="card-text mb-0">
                    In an age where cyber threats are becoming more
                    sophisticated, knowing what not to click is your first line
                    of defense. Opening an unexpected attachment or downloading
                    a file from someone you don't know could be all it takes to
                    compromise your personal data.
                  </p>
                </div>
              </div>
            </section>

            {/* Introduction */}
            <section className="mb-5">
              <p className="lead text-muted">
                While not all files are dangerous, some are commonly used as
                vehicles for malware. Below, we break down{" "}
                <strong>three high-risk file types</strong> you should never
                open from a stranger.
              </p>
            </section>

            {/* EXE Files Section */}
            <section className="mb-5">
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-0 py-3">
                  <h3 className="h2 fw-bold text-dark mb-0">
                    <span className="badge bg-danger me-3">1</span>
                    <code>.EXE</code> (Executable Files)
                  </h3>
                </div>
                <div className="card-body">
                  <div className="row g-4">
                    <div className="col-md-4">
                      <div className="text-center p-3 bg-light rounded">
                        <div className="text-danger mb-2">
                          <strong>Danger Level</strong>
                        </div>
                        <div className="h4 text-danger">🚨 Very High</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-center p-3 bg-light rounded">
                        <div className="text-dark mb-2">
                          <strong>Common Disguise</strong>
                        </div>
                        <div>Free tools, games, installers</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-center p-3 bg-light rounded">
                        <div className="text-dark mb-2">
                          <strong>File Type</strong>
                        </div>
                        <div>Windows Executable</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <h5 className="fw-bold text-dark mb-3">Threat Analysis</h5>
                    <p>
                      These are programs that execute code directly on your
                      computer. Hackers often disguise malware as helpful tools
                      or entertaining apps in <code>.exe</code> form. Once
                      clicked, the program can install:
                    </p>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className="card h-100 border-danger border-2">
                          <div className="card-body text-center">
                            <div className="text-danger mb-2">🕵️</div>
                            <h6 className="card-title">Spyware</h6>
                            <p className="card-text small text-muted">
                              Monitors your activities and steals sensitive data
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card h-100 border-danger border-2">
                          <div className="card-body text-center">
                            <div className="text-danger mb-2">🔒</div>
                            <h6 className="card-title">Ransomware</h6>
                            <p className="card-text small text-muted">
                              Encrypts your files and demands payment
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card h-100 border-danger border-2">
                          <div className="card-body text-center">
                            <div className="text-danger mb-2">🚪</div>
                            <h6 className="card-title">Backdoors</h6>
                            <p className="card-text small text-muted">
                              Provides remote access to attackers
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="alert alert-danger mt-4">
                    <strong>🛡️ Security Tip:</strong> Unless you absolutely
                    trust the source, never open a <code>.exe</code> file sent
                    to you unexpectedly. Always verify the sender and use
                    antivirus software to scan suspicious files.
                  </div>
                </div>
              </div>
            </section>

            {/* DOCM Files Section */}
            <section className="mb-5">
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-0 py-3">
                  <h3 className="h2 fw-bold text-dark mb-0">
                    <span className="badge bg-warning me-3">2</span>
                    <code>.DOCM</code> (Macro-Enabled Documents)
                  </h3>
                </div>
                <div className="card-body">
                  <div className="row g-4">
                    <div className="col-md-4">
                      <div className="text-center p-3 bg-light rounded">
                        <div className="text-warning mb-2">
                          <strong>Danger Level</strong>
                        </div>
                        <div className="h4 text-warning">⚠️ High</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-center p-3 bg-light rounded">
                        <div className="text-dark mb-2">
                          <strong>Common Disguise</strong>
                        </div>
                        <div>Resumes, contracts, official forms</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-center p-3 bg-light rounded">
                        <div className="text-dark mb-2">
                          <strong>File Type</strong>
                        </div>
                        <div>Microsoft Word with Macros</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <h5 className="fw-bold text-dark mb-3">Threat Analysis</h5>
                    <p>
                      These files can contain embedded scripts (macros) that
                      execute harmful code as soon as the document is opened.
                      Cybercriminals often use this format in targeted phishing
                      campaigns because:
                    </p>
                    <ul className="list-unstyled">
                      <li className="mb-2">
                        • <strong>Appears legitimate:</strong> Looks like normal
                        Office documents
                      </li>
                      <li className="mb-2">
                        • <strong>Social engineering:</strong> Uses urgency or
                        importance to trick users
                      </li>
                      <li className="mb-2">
                        • <strong>Bypasses filters:</strong> Often gets through
                        email security
                      </li>
                    </ul>
                  </div>

                  <div className="alert alert-warning mt-4">
                    <strong>🛡️ Security Tip:</strong> If a Word document asks
                    you to "Enable Macros," close it immediately unless you are
                    100% sure of its legitimacy. Consider disabling macros
                    entirely in your Office settings.
                  </div>
                </div>
              </div>
            </section>

            {/* ZIP/RAR Files Section */}
            <section className="mb-5">
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-0 py-3">
                  <h3 className="h2 fw-bold text-dark mb-0">
                    <span className="badge bg-info me-3">3</span>
                    <code>.ZIP</code> and <code>.RAR</code> (Compressed
                    Archives)
                  </h3>
                </div>
                <div className="card-body">
                  <div className="row g-4">
                    <div className="col-md-4">
                      <div className="text-center p-3 bg-light rounded">
                        <div className="text-info mb-2">
                          <strong>Danger Level</strong>
                        </div>
                        <div className="h4 text-info">⚠️ Medium to High</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-center p-3 bg-light rounded">
                        <div className="text-dark mb-2">
                          <strong>Common Disguise</strong>
                        </div>
                        <div>Invoices, receipts, e-tickets</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-center p-3 bg-light rounded">
                        <div className="text-dark mb-2">
                          <strong>File Type</strong>
                        </div>
                        <div>Compressed Archive</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <h5 className="fw-bold text-dark mb-3">Threat Analysis</h5>
                    <p>
                      Compressed files are used to bundle multiple files
                      together and can conceal malicious content like trojans,
                      worms, or keyloggers. Since you often can't see what
                      you're opening until it's too late, they pose a hidden
                      risk.
                    </p>

                    <div className="bg-light p-4 rounded">
                      <h6 className="fw-bold text-dark mb-3">
                        Common Attack Vectors
                      </h6>
                      <div className="row">
                        <div className="col-md-6">
                          <ul className="list-unstyled">
                            <li className="mb-2">
                              <strong>Password Protection:</strong> Hides
                              content from scanners
                            </li>
                            <li className="mb-2">
                              <strong>Nested Archives:</strong> Multiple layers
                              of compression
                            </li>
                            <li className="mb-2">
                              <strong>File Spoofing:</strong> Fake file
                              extensions
                            </li>
                          </ul>
                        </div>
                        <div className="col-md-6">
                          <ul className="list-unstyled">
                            <li className="mb-2">
                              <strong>Oversized Files:</strong> Contains
                              malicious payloads
                            </li>
                            <li className="mb-2">
                              <strong>Urgent Language:</strong> Creates false
                              urgency
                            </li>
                            <li className="mb-2">
                              <strong>Fake Senders:</strong> Spoofed email
                              addresses
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="alert alert-info mt-4">
                    <strong>🛡️ Security Tip:</strong> Avoid extracting any
                    compressed file from an unknown sender, especially if the
                    email or message uses urgent language. Use cloud-based
                    scanning services for suspicious archives.
                  </div>
                </div>
              </div>
            </section>

            {/* Security Best Practices */}
            <section className="mb-5">
              <h2 className="h2 fw-bold text-dark mb-4 border-bottom pb-2">
                🔒 Stay Safe: Comprehensive Security Practices
              </h2>

              <div className="row g-4">
                <div className="col-md-6">
                  <div className="card h-100 border-success border-2">
                    <div className="card-body">
                      <h5 className="card-title fw-bold text-success">
                        Prevention Strategies
                      </h5>
                      <ul className="list-unstyled">
                        <li className="mb-3">
                          <strong>🛡️ Software Updates</strong>
                          <br />
                          <small className="text-muted">
                            Keep antivirus and anti-malware software up to date
                            with latest definitions
                          </small>
                        </li>
                        <li className="mb-3">
                          <strong>📬 Email Vigilance</strong>
                          <br />
                          <small className="text-muted">
                            Be skeptical of unsolicited attachments and verify
                            senders
                          </small>
                        </li>
                        <li className="mb-3">
                          <strong>🔎 Link Inspection</strong>
                          <br />
                          <small className="text-muted">
                            Hover over links to check URLs before clicking
                          </small>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card h-100 border-primary border-2">
                    <div className="card-body">
                      <h5 className="card-title fw-bold text-primary">
                        Response Actions
                      </h5>
                      <ul className="list-unstyled">
                        <li className="mb-3">
                          <strong>🗑️ Safe Disposal</strong>
                          <br />
                          <small className="text-muted">
                            When in doubt, delete the file and empty trash
                          </small>
                        </li>
                        <li className="mb-3">
                          <strong>📊 Backup Regularly</strong>
                          <br />
                          <small className="text-muted">
                            Maintain current backups to recover from ransomware
                          </small>
                        </li>
                        <li className="mb-3">
                          <strong>🚨 Incident Reporting</strong>
                          <br />
                          <small className="text-muted">
                            Report suspicious files to your IT department
                          </small>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card border-0 bg-light mt-4">
                <div className="card-body text-center">
                  <blockquote className="blockquote mb-0">
                    <p className="mb-0 fst-italic">
                      "It's better to lose a potential cat video than your
                      entire digital identity."
                    </p>
                    <footer className="blockquote-footer mt-2">
                      Cyber Security Expert
                    </footer>
                  </blockquote>
                </div>
              </div>
            </section>

            {/* Quick Reference Table */}
            <section className="mb-5">
              <h2 className="h2 fw-bold text-dark mb-4 border-bottom pb-2">
                Quick Reference Guide
              </h2>

              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th scope="col" className="fw-bold text-dark">
                        File Type
                      </th>
                      <th scope="col" className="fw-bold text-dark">
                        Danger Level
                      </th>
                      <th scope="col" className="fw-bold text-dark">
                        Common Disguise
                      </th>
                      <th scope="col" className="fw-bold text-dark">
                        Primary Threat
                      </th>
                      <th scope="col" className="fw-bold text-dark">
                        Action Required
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <strong>
                          <code>.EXE</code>
                        </strong>
                      </td>
                      <td>
                        <span className="badge bg-danger">Very High</span>
                      </td>
                      <td>Software, Games</td>
                      <td>System Compromise</td>
                      <td>
                        <span className="badge bg-danger">
                          Delete Immediately
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>
                          <code>.DOCM</code>
                        </strong>
                      </td>
                      <td>
                        <span className="badge bg-warning">High</span>
                      </td>
                      <td>Documents, Forms</td>
                      <td>Macro Malware</td>
                      <td>
                        <span className="badge bg-warning">
                          Do Not Enable Macros
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>
                          <code>.ZIP/.RAR</code>
                        </strong>
                      </td>
                      <td>
                        <span className="badge bg-info">Medium-High</span>
                      </td>
                      <td>Archives, Bundles</td>
                      <td>Hidden Payloads</td>
                      <td>
                        <span className="badge bg-info">
                          Scan Before Opening
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Conclusion */}
            <section className="mb-5">
              <div
                className="card border-0 text-white"
                style={{
                  background:
                    "linear-gradient(135deg, #dc3545 0%, #8b0000 100%)",
                }}
              >
                <div className="card-body p-4">
                  <h3 className="h4 fw-bold mb-3">
                    Final Security Recommendation
                  </h3>
                  <p className="mb-4 opacity-90">
                    Cybersecurity is as much about common sense as it is about
                    software. By understanding these high-risk file types and
                    implementing proper security practices, you can
                    significantly reduce your vulnerability to cyber attacks.
                    Remember: when in doubt, throw it out.
                  </p>
                  <div className="d-flex align-items-center justify-content-center">
                    <div className="bg-white bg-opacity-20 text-black rounded p-3 me-4">
                      <strong className="d-block h5 mb-1">90%</strong>
                      <small className="opacity-90">Attack Prevention</small>
                    </div>
                    <div className="bg-white text-black bg-opacity-20 rounded p-3">
                      <strong className="d-block h5 mb-1">Zero Trust</strong>
                      <small className="opacity-90">Security Mindset</small>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Resources */}
            <footer className="border-top pt-4">
              <h6 className="fw-bold text-dark mb-3">
                Security Resources & Tags
              </h6>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <span className="badge bg-secondary">#CyberSecurity</span>
                <span className="badge bg-secondary">#OnlineSafety</span>
                <span className="badge bg-secondary">#TechTips</span>
                <span className="badge bg-secondary">#MalwareAlert</span>
                <span className="badge bg-secondary">#PhishingProtection</span>
                <span className="badge bg-secondary">#DataSecurity</span>
              </div>
            </footer>
          </section>
        </div>
      </article>
    </div>
  );
}
